import { createContext, useContext, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getThemeStorageKey, normalizeTheme, persistTheme, readSavedTheme, syncThemeToDocument } from '../../utils/themeUtils';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const storageKey = useMemo(
    () => getThemeStorageKey(location.pathname),
    [location.pathname]
  );
  const [theme, setTheme] = useState(() => readSavedTheme(storageKey));
  const appliedStorageKey = useRef(null);

  useLayoutEffect(() => {
    // Child layout effects may already have applied the authoritative company
    // theme for this scope. Do not overwrite it with a stale/blocked cache.
    if (appliedStorageKey.current === storageKey) return;
    const savedTheme = readSavedTheme(storageKey);
    syncThemeToDocument(savedTheme);
    appliedStorageKey.current = storageKey;
    setTheme(savedTheme);
  }, [storageKey]);

  const applyTheme = useCallback((themeName) => {
    const nextTheme = normalizeTheme(themeName);

    syncThemeToDocument(nextTheme);
    appliedStorageKey.current = storageKey;
    setTheme((prev) => (prev === nextTheme ? prev : nextTheme));

    persistTheme(storageKey, nextTheme);
  }, [storageKey]);

  const value = useMemo(() => ({ theme, applyTheme }), [theme, applyTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
